import React, { useCallback, ReactNode, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useLoginMutation, useLogoutMutation, authApi, LoginResponse } from '@/services/authApi';
import { baseApi } from '@/services/baseApi';
import { setCredentials, setError, setLoading, logout as logoutAction } from '@/store/slices/authSlice';
import type { RootState } from '@/store';
import { store } from '@/store';
import { UserRole } from '@/types';
import { AuthContext } from './auth-context.types';

const mapAccountTypeToRole = (accountType?: string): UserRole => {
  if (!accountType) return UserRole.LEARNER;
  const type = accountType.toLowerCase();
  if (type.includes('admin') || type.includes('staff')) return UserRole.ADMIN;
  if (type.includes('instructor') || type.includes('teacher')) return UserRole.INSTRUCTOR;
  if (type.includes('student') || type.includes('learner')) return UserRole.LEARNER;
  return UserRole.LEARNER;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state: RootState) => state.auth);
  const [loginMutation] = useLoginMutation();
  const [logoutMutation] = useLogoutMutation();

  // Initialize: If we have a token, verify it and fetch fresh user data
  useEffect(() => {
    let isMounted = true;
    
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      const storedUser = localStorage.getItem('user');
      
      // If we have a token, verify it and get fresh user data
      if (token) {
        dispatch(setLoading(true));
        
        try {
          // Flag to prevent baseApi from auto-logging out during initialization
          (window as any).__AUTH_INITIALIZING__ = true;
          
          const userResult = await store.dispatch(
            authApi.endpoints.getAuthUserProfile.initiate()
          );
          
          if (!isMounted) return;
          
          // Check if we got an error response
          if ('error' in userResult) {
            const error = userResult.error as any;
            const isAuthError = error?.status === 401 || error?.status === 403;
            
            if (isAuthError) {
              // Token is invalid, clear auth state
              console.warn('Token is invalid during initialization, clearing auth state');
              dispatch(logoutAction());
            } else if (storedUser) {
              // Network error but we have stored user - keep it (might be temporary network issue)
              console.warn('Failed to verify token, keeping stored user data');
              try {
                const parsedUser = JSON.parse(storedUser);
                dispatch(
                  setCredentials({
                    user: parsedUser,
                    token: token,
                    refreshToken: refreshToken || undefined,
                  })
                );
              } catch (parseError) {
                console.error('Error parsing stored user:', parseError);
                dispatch(logoutAction());
              }
            } else {
              // No stored user and error occurred, clear auth
              dispatch(logoutAction());
            }
          } else if ('data' in userResult && userResult.data) {
            // Success - normalize profile response shape
            const payload = userResult.data as any;
            const profile = (payload.response || payload.data || payload) as any;
            const mappedUser = {
              ...profile,
              role: profile.role || mapAccountTypeToRole(profile.accountType)
            };
            dispatch(
              setCredentials({
                user: mappedUser,
                token: token,
                refreshToken: refreshToken || undefined,
              })
            );
          } else {
            // Unexpected response format, clear auth
            console.warn('Unexpected response format, clearing auth state');
            dispatch(logoutAction());
          }
        } catch (error: any) {
          console.error('Unexpected error initializing auth:', error);
          // For unexpected errors, if we have stored user, keep it
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              dispatch(
                setCredentials({
                  user: parsedUser,
                  token: token,
                  refreshToken: refreshToken || undefined,
                })
              );
            } catch (parseError) {
              console.error('Error parsing stored user:', parseError);
              dispatch(logoutAction());
            }
          } else {
            dispatch(logoutAction());
          }
        } finally {
          (window as any).__AUTH_INITIALIZING__ = false;
          if (isMounted) {
            dispatch(setLoading(false));
          }
        }
      } else {
        dispatch(setLoading(false));
      }
    };

    initializeAuth();
    
    return () => {
      isMounted = false;
      (window as any).__AUTH_INITIALIZING__ = false;
    };
  }, []); // Only run once on mount

  const login = useCallback(
    async (email: string, password: string, rememberMe: boolean = true) => {
      try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        const result = await loginMutation({ email: email.trim(), password: password.trim() }).unwrap();
        
        const apiResult = result.response as LoginResponse;
        // Handle 2FA requirement
        if (result.success && apiResult.requiresTwoFactor) {
          return apiResult;
        }
        
        if (result.success && apiResult.accessToken) {
          const { accessToken, refreshToken } = apiResult;
          
          // First, set the token so we can make authenticated requests
          // We'll fetch the user data next, but we need a temporary user object
          // The updateUser will be called after fetching the actual user data
          dispatch(
            setCredentials({
              user: {
                id: '',
                email: email.trim(),
                firstName: '',
                lastName: '',
                role: UserRole.LEARNER,
                isVerified: apiResult.isEmailVerified || false,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              },
              token: accessToken,
              refreshToken: refreshToken,
              rememberMe,
            })
          );

          // Now fetch the user data using the token we just received
          try {
            const userResult = await store.dispatch(
              authApi.endpoints.getAuthUserProfile.initiate()
            );
            
            if ('data' in userResult && userResult.data) {
              const payload = userResult.data as any;
              const profile = (payload.response || payload.data || payload) as any;
              const mappedUser = {
                ...profile,
                role: profile.role || mapAccountTypeToRole(profile.accountType)
              };
              // Replace the entire user object with the fetched data
              dispatch(
                setCredentials({
                  user: mappedUser,
                  token: accessToken,
                  refreshToken: refreshToken,
                  rememberMe,
                })
              );
            } else {
              console.warn('Failed to fetch user data after login');
            }
          } catch (userError) {
            console.error('Error fetching user data:', userError);
            // Don't fail the login if user fetch fails - user can be fetched later
          }
          
          return apiResult;
        } else {
          const errorMsg = (result.message) || 'Login failed. Please check your credentials.';
          console.error("Login failed - API response:", result);
          throw new Error(errorMsg);
        }
      } catch (error: unknown) {
        let errorMessage = 'Failed to sign in. Please check your credentials.';
        
        console.error("Login error caught:", error);
        
        // Handle RTK Query errors
        if (error && typeof error === 'object') {
          // RTK Query errors have a 'data' property with the API response
          if ('data' in error && error.data && typeof error.data === 'object') {
            const errorData = error.data as Record<string, unknown>;
            
            // Check for ApiResponse format: { success: false, message: "...", data: null }
            if ('message' in errorData && typeof errorData.message === 'string') {
              errorMessage = errorData.message;
            } 
            // Check for nested error format: { error: { message: "..." } }
            else if ('error' in errorData && errorData.error && typeof errorData.error === 'object') {
              const nestedError = errorData.error as Record<string, unknown>;
              if ('message' in nestedError && typeof nestedError.message === 'string') {
                errorMessage = nestedError.message;
              }
            }
          }
          // Fallback to standard Error message
          else if ('message' in error && typeof error.message === 'string') {
            errorMessage = error.message;
          }
        }
        
        console.error("Final error message:", errorMessage);
        dispatch(setError(errorMessage));
        throw new Error(errorMessage);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, loginMutation]
  );

  const loginWithToken = useCallback(
    async (accessToken: string, refreshToken?: string) => {
      try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        // Temporarily store the token so getProfile can use it
        dispatch(
          setCredentials({
            user: {
              id: '',
              email: '',
              firstName: '',
              lastName: '',
              role: UserRole.LEARNER,
              isVerified: false,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            token: accessToken,
            refreshToken,
          })
        );

        // Flag to prevent baseApi from auto-logging out during initialization
        (window as any).__AUTH_INITIALIZING__ = true;

        // Fetch the real user profile with the new token
        const userResult = await store.dispatch(
          authApi.endpoints.getAuthUserProfile.initiate(undefined, { forceRefetch: true })
        );

        if ('data' in userResult && userResult.data) {
          const payload = userResult.data as any;
          const profile = (payload.response || payload.data || payload) as any;
          const mappedUser = {
            ...profile,
            role: profile.role || mapAccountTypeToRole(profile.accountType)
          };
          dispatch(
            setCredentials({
              user: mappedUser,
              token: accessToken,
              refreshToken,
            })
          );
          return mappedUser;
        } else {
          throw new Error('Failed to fetch user profile after OAuth login.');
        }
      } catch (error: any) {
        dispatch(logoutAction());
        const message =
          error?.message || 'OAuth login failed. Please try again.';
        dispatch(setError(message));
        throw new Error(message);
      } finally {
        (window as any).__AUTH_INITIALIZING__ = false;
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  const logout = useCallback(async () => {
    try {
      const refreshToken = authState.refreshToken;
      
      // Call the logout API if we have a refresh token
      if (refreshToken) {
        try {
          await logoutMutation({ refreshToken }).unwrap();
        } catch (error) {
          // Even if the API call fails, we should still clear local state
          console.error('Logout API call failed:', error);
        }
      }
      
      // Clear local state regardless of API call result
      dispatch(logoutAction());
      dispatch(baseApi.util.resetApiState());
    } catch (error) {
      // Ensure state is cleared even if there's an error
      console.error('Error during logout:', error);
      dispatch(logoutAction());
    }
  }, [dispatch, logoutMutation, authState.refreshToken]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: authState.isAuthenticated,
        user: authState.user,
        isLoading: authState.isLoading,
        login,
        loginWithToken,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}


